import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { Plus, Search, MessageSquare, ChevronsLeft } from 'lucide-react';
import ministryOfLaw from '../../assets/ministry_of_law.jpeg';

interface HistoryBarProps {
    isOpen?: boolean;
    onClose?: () => void;
    onToggle?: () => void;
}

interface ChatHistory {
    id: string;
    title: string;
    timestamp: number;
    apiResponse?: any;
}

const HistoryBar: React.FC<HistoryBarProps> = ({ isOpen = true, onClose, onToggle }) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
    const [sidebarWidth, setSidebarWidth] = useState<number>(280);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const historyBarRef = useRef<HTMLDivElement>(null);

    // Load history from localStorage
    useEffect(() => {
        const loadHistory = () => {
            const savedHistory = localStorage.getItem('verdictai_chat_history');
            if (savedHistory) {
                try {
                    setChatHistory(JSON.parse(savedHistory));
                } catch (e) {
                    console.error("Failed to parse chat history", e);
                }
            }
        };

        loadHistory();

        // Listen for storage events (in case updated from other tabs or components)
        window.addEventListener('storage', loadHistory);
        return () => window.removeEventListener('storage', loadHistory);
    }, []);

    // Resizing logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;

            const newWidth = e.clientX;
            if (newWidth >= 200 && newWidth <= 480) {
                setSidebarWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    // Group chats by time period
    const groupedChats = {
        today: chatHistory.filter(chat => {
            const today = new Date();
            const chatDate = new Date(chat.timestamp);
            return chatDate.toDateString() === today.toDateString();
        }),
        yesterday: chatHistory.filter(chat => {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const chatDate = new Date(chat.timestamp);
            return chatDate.toDateString() === yesterday.toDateString();
        }),
        last7Days: chatHistory.filter(chat => {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            const chatDate = new Date(chat.timestamp);
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return chatDate > weekAgo && chatDate < yesterday;
        }),
    };

    const handleNewChat = () => {
        navigate('/');
        if (window.innerWidth < 768 && onClose) {
            onClose();
        }
    };

    const handleChatClick = (chat: ChatHistory) => {
        navigate('/search-results', {
            state: {
                searchQuery: chat.title,
                apiResponse: chat.apiResponse
            }
        });
        if (window.innerWidth < 768 && onClose) {
            onClose();
        }
    };

    const getThemeClasses = () => {
        if (theme === 'dark') {
            return {
                bg: 'bg-dark-bg-secondary',
                border: 'border-dark-border',
                hover: 'hover:bg-dark-bg-tertiary',
                text: 'text-dark-text-primary',
                textSecondary: 'text-dark-text-secondary',
            };
        }
        return {
            bg: 'bg-white',
            border: 'border-neutral-200',
            hover: 'hover:bg-neutral-50',
            text: 'text-neutral-900',
            textSecondary: 'text-neutral-600',
        };
    };

    const themeClasses = getThemeClasses();

    if (!isOpen) {
        return (
            <div
                className={`
          ${themeClasses.bg}
          fixed top-0 left-0 h-screen w-16
          border-r ${themeClasses.border}
          flex flex-col items-center
          py-4 gap-4
          z-40
          hidden md:flex
          transition-all duration-300
        `}
            >
                {/* Open Sidebar Button */}
                <button
                    onClick={onToggle}
                    className={`
            p-3 rounded-lg
            ${themeClasses.hover}
            ${themeClasses.textSecondary}
            transition-all
            hover:scale-110
          `}
                    title="Open sidebar"
                >
                    <ChevronsLeft className="rotate-180" size={20} />
                </button>

                {/* New Chat Icon */}
                <button
                    onClick={handleNewChat}
                    className={`
            p-3 rounded-lg
            ${themeClasses.hover}
            ${themeClasses.textSecondary}
            transition-all
            hover:scale-110
          `}
                    title="New chat"
                >
                    <Plus size={20} />
                </button>

                {/* Search Icon */}
                <button
                    className={`
            p-3 rounded-lg
            ${themeClasses.hover}
            ${themeClasses.textSecondary}
            transition-all
            hover:scale-110
          `}
                    title="Search chats"
                >
                    <Search size={20} />
                </button>
            </div>
        );
    }

    return (
        <>
            <div
                ref={historyBarRef}
                style={{ width: `${sidebarWidth}px` }}
                className={`
          ${themeClasses.bg}
          fixed top-0 left-0 h-screen
          border-r ${themeClasses.border}
          flex flex-col
          z-40
          transition-all duration-300
        `}
            >
                {/* Header Section */}
                <div className={`p-4 border-b ${themeClasses.border}`}>
                    {/* Top Bar with Logo and Close Button */}
                    <div className="flex items-center justify-between mb-4">
                        <img
                            src={ministryOfLaw}
                            alt="Ministry of Law & Justice"
                            className="h-12 object-contain"
                        />
                        <button
                            onClick={onToggle || onClose}
                            className={`
                p-2 rounded-lg
                ${themeClasses.hover}
                ${themeClasses.textSecondary}
                transition-colors
                hover:scale-105
              `}
                            aria-label="Close sidebar"
                            title="Close sidebar"
                        >
                            <ChevronsLeft size={20} />
                        </button>
                    </div>

                    {/* New Chat Button */}
                    <button
                        onClick={handleNewChat}
                        className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-lg
              ${theme === 'dark'
                                ? 'bg-dark-bg-tertiary hover:bg-dark-bg-elevated text-dark-text-primary'
                                : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900'}
              transition-all duration-200
              font-medium text-sm
              shadow-sm hover:shadow-md
            `}
                    >
                        <Plus size={18} />
                        <span>New Chat</span>
                    </button>

                    {/* Search Chats */}
                    <div className="mt-3 relative">
                        <Search
                            size={16}
                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary}`}
                        />
                        <input
                            type="text"
                            placeholder="Search chats..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={`
                w-full pl-10 pr-4 py-2.5 rounded-lg text-sm
                ${theme === 'dark'
                                    ? 'bg-dark-bg-tertiary border-dark-border text-dark-text-primary placeholder-dark-text-tertiary'
                                    : 'bg-neutral-50 border-neutral-200 text-neutral-900 placeholder-neutral-500'}
                border focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50
                transition-all
              `}
                        />
                    </div>
                </div>

                {/* Chat History */}
                <div className="flex-1 overflow-y-auto px-2 py-4">
                    {chatHistory.length === 0 ? (
                        /* Empty State */
                        <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                            <MessageSquare
                                size={48}
                                className={`${themeClasses.textSecondary} opacity-40 mb-4`}
                            />
                            <p className={`${themeClasses.textSecondary} text-sm font-medium`}>
                                No conversations yet
                            </p>
                            <p className={`${themeClasses.textSecondary} text-xs mt-2 opacity-70`}>
                                Start a new chat to begin your legal research
                            </p>
                        </div>
                    ) : (
                        /* Chat List */
                        <div className="space-y-6">
                            {groupedChats.today.length > 0 && (
                                <div>
                                    <h3 className={`px-3 text-xs font-semibold ${themeClasses.textSecondary} uppercase mb-2`}>
                                        Today
                                    </h3>
                                    <div className="space-y-1">
                                        {groupedChats.today.map(chat => (
                                            <button
                                                key={chat.id}
                                                onClick={() => handleChatClick(chat)}
                                                className={`
                          w-full text-left px-3 py-2.5 rounded-lg
                          ${themeClasses.hover}
                          ${themeClasses.text}
                          transition-all duration-150
                          group
                        `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <MessageSquare size={16} className={themeClasses.textSecondary} />
                                                    <span className="text-sm truncate flex-1">{chat.title}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {groupedChats.yesterday.length > 0 && (
                                <div>
                                    <h3 className={`px-3 text-xs font-semibold ${themeClasses.textSecondary} uppercase mb-2`}>
                                        Yesterday
                                    </h3>
                                    <div className="space-y-1">
                                        {groupedChats.yesterday.map(chat => (
                                            <button
                                                key={chat.id}
                                                onClick={() => handleChatClick(chat)}
                                                className={`
                          w-full text-left px-3 py-2.5 rounded-lg
                          ${themeClasses.hover}
                          ${themeClasses.text}
                          transition-all duration-150
                          group
                        `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <MessageSquare size={16} className={themeClasses.textSecondary} />
                                                    <span className="text-sm truncate flex-1">{chat.title}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {groupedChats.last7Days.length > 0 && (
                                <div>
                                    <h3 className={`px-3 text-xs font-semibold ${themeClasses.textSecondary} uppercase mb-2`}>
                                        Previous 7 Days
                                    </h3>
                                    <div className="space-y-1">
                                        {groupedChats.last7Days.map(chat => (
                                            <button
                                                key={chat.id}
                                                onClick={() => handleChatClick(chat)}
                                                className={`
                          w-full text-left px-3 py-2.5 rounded-lg
                          ${themeClasses.hover}
                          ${themeClasses.text}
                          transition-all duration-150
                          group
                        `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <MessageSquare size={16} className={themeClasses.textSecondary} />
                                                    <span className="text-sm truncate flex-1">{chat.title}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>



                {/* Resize Handle */}
                <div
                    onMouseDown={() => setIsResizing(true)}
                    className={`
            absolute top-0 right-0 h-full w-1 cursor-ew-resize
            hover:bg-primary-500 hover:w-1.5
            transition-all
            ${isResizing ? 'bg-primary-500 w-1.5' : 'bg-transparent'}
          `}
                    title="Drag to resize"
                />
            </div>

            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
                    onClick={onClose}
                />
            )}
        </>
    );
};

export default HistoryBar;